import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { cacheKeys, useFetcher } from '../../graphql';

export const PARTICIPANTS_PAGE_SIZE = 50;

export type ParticipantAccount = {
  id: string;
  name?: { first?: string | null; last?: string | null } | null;
  avatar?: {
    id: string;
    extension?: string | null;
    status?: string | null;
  } | null;
};

export type Participant = {
  account: ParticipantAccount;
};

export type ParticipantsPage = {
  count: number;
  limit: number | null;
  offset: number | null;
  items: Participant[];
};

export type UseParticipantsArgs = {
  workspaceAssetId: string;
  pageSize?: number;
  /** Withholds the request — e.g. until the caller has its own subject resolved. Defaults to true. */
  enabled?: boolean;
};

/**
 * The one react-query key this hook's pages live under. `assetParticipants`
 * takes an id + PageRequest, not a graphback-style filter, so it can't share
 * `infiniteFindByQueryCacheKey`'s shape — this mirrors its intent (every input
 * that changes the result is part of the key) using the same `cacheKeys`
 * factory.
 */
export const participantsCacheKey = ({
  workspaceAssetId,
  pageSize = PARTICIPANTS_PAGE_SIZE,
}: Pick<UseParticipantsArgs, 'workspaceAssetId' | 'pageSize'>) =>
  cacheKeys('participant').list({ workspaceAssetId, pageSize });

/**
 * The paged `assetParticipants` read behind the Participants band: who can
 * see one workspace-asset. Server-authorized and server-ordered (name, then
 * id); this hook only pages and flattens.
 */
export const useParticipants = ({
  workspaceAssetId,
  pageSize = PARTICIPANTS_PAGE_SIZE,
  enabled = true,
}: UseParticipantsArgs) => {
  const fetcher = useFetcher<
    { assetParticipants: ParticipantsPage },
    { id: string; page: { limit: number; offset: number } }
  >(FIND_ASSET_PARTICIPANTS);

  const {
    data,
    isLoading,
    isError,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: participantsCacheKey({ workspaceAssetId, pageSize }),
    queryFn: async ({
      pageParam,
    }: {
      pageParam: { offset: number };
    }): Promise<ParticipantsPage> => {
      const result = await fetcher({
        id: workspaceAssetId,
        page: { limit: pageSize, offset: pageParam.offset },
      });
      return result.assetParticipants;
    },
    enabled: enabled && !!workspaceAssetId,
    placeholderData: keepPreviousData,
    initialPageParam: { offset: 0 },
    getNextPageParam: (lastPage: ParticipantsPage) => {
      const offset = lastPage.offset ?? 0;
      const limit = lastPage.limit ?? pageSize;
      if (offset + limit >= lastPage.count) return undefined;
      return { offset: offset + limit };
    },
  });

  // Deduplicated by account id, not a plain flatMap — mirrors
  // useAssetChatFeed's convention. A page fetched while an earlier one is
  // still current can shift rows across the offset boundary and repeat an
  // account on two pages; a repeated key is a hard collision for a reader
  // that lists these by id. First occurrence wins.
  const items = useMemo(() => {
    const seen = new Set<string>();
    const deduped: Participant[] = [];
    data?.pages.forEach((page) => {
      page.items?.forEach((item) => {
        if (seen.has(item.account.id)) return;
        seen.add(item.account.id);
        deduped.push(item);
      });
    });
    return deduped;
  }, [data]);

  return {
    items,
    count: data?.pages[0]?.count,
    isLoading,
    isError,
    error,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  };
};

export const FIND_ASSET_PARTICIPANTS = `
  query assetParticipants($id: ID!, $page: PageRequest) {
    assetParticipants(workspaceAssetId: $id, page: $page) {
      count
      limit
      offset
      items {
        account {
          id
          name {
            first
            last
          }
          avatar {
            id
            extension
            status
          }
        }
      }
    }
  }
`;
